import React, { useCallback, useState } from "react";

import SearchFormSort from "./search_form_sort";
import SearchFormCalendar from "./search_form_calendar";
import SearchFormDatePickerButton from "./search_form_date_picker_button";
import ModalContentBase from "../modal/hooks/modal_content_base";

// イベント並べ替え
// app/controllers/concerns/search_concern.rbと一致
const sortEventKeys = {
  poplular: "popular",
  recently: "recently",
  weekend: "weekend",
  new: "new",
  circle: "circle",
  newCircle: "new_circle",
};

// 検索ワード補完
const searchTypes = { ec: "eventCondition", kw: "keyword" };
const searchCompletions = {
  人気イベント: {
    searchType: searchTypes.ec,
    sortEventKey: sortEventKeys.poplular,
  },
  オンライン: { searchType: searchTypes.kw },
};

// 画面上部の並べ替えショートカットボタンの表示ラベル。押すと従来通り人気順で
// loadSearchResultするのは変わらないが、現在選択中の並べ替えが分かるよう表示だけ切り替える。
const sortShortcutLabels = {
  [sortEventKeys.recently]: "開催順",
  [sortEventKeys.new]: "新着",
};

const SearchForm = ({
  // categories,
  keyword,
  loadSearchResult,
  eventCondition,
  eventDate,
  timeSlot,
  companyId,
}) => {
  const [keywordDisp, setKeywordDisp] = useState(keyword);
  // 「別条件で検索する」モーダルの開閉。キーワード検索・別日を選択・開催順/新着/時間帯フィルタは
  // このモーダルの中に移設した（見た目のみの変更で、loadSearchResultの呼び出し方は変えていない）。
  const [isMoreConditionsOpen, setIsMoreConditionsOpen] = useState(false);

  // 補完（入力時に自動ロード）
  const onSearchCompletion = useCallback(
    (value) => {
      const sc = searchCompletions[value];
      if (sc.searchType === searchTypes.ec) {
        setKeywordDisp("");
        loadSearchResult({
          keyword: "",
          circleOrder: "",
          eventCondition: sc.sortEventKey,
          eventDate: null,
        });
        return false;
      } else if (sc.searchType === searchTypes.kw) {
        loadSearchResult({
          circleOrder: "",
          eventCondition: "",
          keyword: value,
        });
      }
    },
    [setKeywordDisp, loadSearchResult]
  );

  // キーワード入力
  const _onChangeKeyword = useCallback(
    (e) => {
      if (Object.keys(searchCompletions).indexOf(e.target.value) !== -1) {
        onSearchCompletion(e.target.value); // datalistタグ補完時に自動ロード
      }
      setKeywordDisp(e.target.value);
    },
    [onSearchCompletion, setKeywordDisp]
  );

  // キーワード検索
  const _onKeyDown = useCallback(
    (e) => {
      if (e.keyCode === 13) {
        loadSearchResult({ keyword: keywordDisp });
        return false;
      }
    },
    [loadSearchResult, keywordDisp]
  );

  // 日付検索
  const eventDateFunc = useCallback(
    (dateStr) => {
      loadSearchResult({
        keyword: keywordDisp,
        circleOrder: "",
        eventCondition: eventCondition,
        eventDate: dateStr,
      });
    },
    [keywordDisp, loadSearchResult]
  );

  // 時間帯フィルタ変更
  const onChangeTimeSlot = useCallback(
    (newTimeSlot, customRange) => {
      const params = {
        keyword: keywordDisp,
        circleOrder: "",
        eventCondition: eventCondition,
        eventDate: eventDate,
        timeSlot: newTimeSlot,
      };
      if (newTimeSlot === "custom" && customRange) {
        params.timeSlotFrom = customRange.from;
        params.timeSlotTo = customRange.to;
      }
      loadSearchResult(params);
    },
    [keywordDisp, eventCondition, eventDate, loadSearchResult]
  );

  // ソート検索
  const sortEventFunc = useCallback(
    (sortEventKey) => {
      if (sortEventKey === sortEventKeys.poplular) {
        loadSearchResult({
          keyword: "",
          circleOrder: "",
          eventCondition: "",
          eventDate: eventDate,
        });
        setKeywordDisp("");
      } else {
        loadSearchResult({
          keyword: keywordDisp,
          circleOrder: "",
          eventCondition: sortEventKey,
          eventDate: eventDate,
        });
      }
    },
    [sortEventKeys, keywordDisp, loadSearchResult, setKeywordDisp]
  );

  const sortShortcutLabel = sortShortcutLabels[eventCondition] || "人気順";
  // 押すたびに 人気順→開催順→新着→人気順… の順で切り替える
  const nextSortEventKey =
    eventCondition === sortEventKeys.recently
      ? sortEventKeys.new
      : eventCondition === sortEventKeys.new
      ? sortEventKeys.poplular
      : sortEventKeys.recently;

  return (
    <div className="pb-3">
      {eventCondition !== "circle" ? (
        <div className="search-event__body">
          <div className="container">
            <p className="search-event__heading">
              <img
                className="ls-is-cached lazyloaded search-event__heading-icon-2026"
                src="/assets2019/img/icon_calendar.svg"
                alt=""
              />
              {companyId ? "日時でイベントを探そう" : "日時で遊びのイベントを探そう"}
            </p>
            <SearchFormCalendar
              eventDate={eventDate}
              eventDateFunc={eventDateFunc}
              hideDatePickerButton={true}
            />
            <div className="text-center mt-2">
              <button
                type="button"
                className="search-event__more-conditions-btn-2026"
                onClick={() => setIsMoreConditionsOpen(true)}
              >
                別条件で検索する
              </button>
            </div>
            <div className="text-right mt-2 mb-2">
              <button
                type="button"
                className="search-event__popular-link-2026"
                onClick={() => sortEventFunc(nextSortEventKey)}
              >
                <i className="fas fa-sort-amount-down-alt" aria-hidden="true"></i>
                {sortShortcutLabel}
              </button>
            </div>
          </div>
        </div>
      ) : (
        // circle検索モードでは元々日付・ソートは対象外だが、キーワード検索（モーダル内）への
        // 入口だけは従来通りどの検索モードでも使えるように残す。
        <div className="container text-center">
          <button
            type="button"
            className="search-event__more-conditions-btn-2026"
            onClick={() => setIsMoreConditionsOpen(true)}
          >
            別条件で検索する
          </button>
        </div>
      )}

      {isMoreConditionsOpen && (
        <ModalContentBase
          closeModal={() => setIsMoreConditionsOpen(false)}
          contentClassName="search-event__more-conditions-modal-2026"
          modalContent={
            <div className="p-3">
              <div className="row">
                <div className="col">
                  <input
                    type="text"
                    className="m-search-textbox search-event__keyword-input-2026"
                    value={keywordDisp}
                    placeholder={companyId ? "キーワードでイベントを探す" : "キーワードで遊びを探す"}
                    onChange={_onChangeKeyword}
                    onKeyDown={_onKeyDown}
                    autoComplete="on"
                    list="mylist"
                    inputMode="search"
                  />
                </div>
              </div>
              {eventCondition !== "circle" && (
                <>
                  <div className="mt-2 text-center">
                    <SearchFormDatePickerButton
                      eventDate={eventDate}
                      eventDateFunc={eventDateFunc}
                      withPortal={false}
                    />
                  </div>
                  <div className="mt-2">
                    <SearchFormSort
                      sortEventKeys={sortEventKeys}
                      sortEventFunc={sortEventFunc}
                      eventCondition={eventCondition}
                      eventDate={eventDate}
                      eventDateFunc={eventDateFunc}
                      timeSlot={timeSlot}
                      onChangeTimeSlot={onChangeTimeSlot}
                    />
                  </div>
                </>
              )}
            </div>
          }
        />
      )}
    </div>
  );
};

export default SearchForm;
